/**
 * /agente — "Tu agente": las dos formas de conectarse y la puerta al handbook.
 *
 * Es a donde lleva el botón del robot de la barra superior (antes abría Kane
 * directo). Kane OneDeck se sigue abriendo con el mismo flujo SSO
 * (`useKaneAssistant`), pero ahora desde el botón de esta pantalla.
 *
 * Reutiliza `public/guia-agente/*` (QR, Claude/Cursor, guía de uso) enlazando,
 * no copiando. Los pasos de ChatGPT sí se muestran acá porque es lo que el
 * usuario necesita sin salir de la app; la paridad con `conectar.html` la fija
 * `agenteConexionParidad.test.js`.
 *
 * @module pages/agente/AgenteConexion
 */

import React, { useState } from 'react';
import { Container, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import useKaneAssistant from '../../hooks/useKaneAssistant';
import {
  KANE_URL,
  MCP_CONNECTOR_URL,
  PASOS_CHATGPT,
  PLANES_CHATGPT,
  OTROS_ASISTENTES,
  ASISTENTES_NO_DISPONIBLES,
  FUENTES_OPENAI,
  FUENTES_CONSULTADAS_EL,
  PASOS_VERIFICADOS_EL,
} from '../../config/agenteConexion';
import './agente.css';

const publicUrl = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

const AgenteConexion = () => {
  const abrirKane = useKaneAssistant();
  const [copiado, setCopiado] = useState(false);

  const copiarDireccion = async () => {
    try {
      await navigator.clipboard.writeText(MCP_CONNECTOR_URL);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Sin portapapeles (http, permisos): la dirección está visible para copiarla a mano.
      setCopiado(false);
    }
  };

  return (
    <Container className="agente-page py-4" data-testid="agente-conexion">
      <header className="agente-hero">
        <h1 className="agente-title">Tu agente ProMedic</h1>
        <p className="agente-lead">
          Háblale como a un compañero de trabajo. Él se encarga del sistema. Elige cómo quieres
          conectarte.
        </p>
      </header>

      <div className="agente-actions-top">
        <Button as={Link} to="/agente/habilidades" variant="warning" className="agente-cta" data-testid="agente-ir-handbook">
          <i className="bi bi-journal-text me-2" aria-hidden="true"></i>
          Ver qué sabe hacer tu agente
        </Button>
      </div>

      <div className="agente-grid">
        {/* ---------- Opción 1: Kane OneDeck ---------- */}
        <section className="modern-card agente-card agente-card--recomendada" aria-labelledby="agente-kane-t">
          <span className="agente-badge">Recomendada</span>
          <h2 id="agente-kane-t" className="agente-card-title">
            <span className="agente-hex">1</span> Kane OneDeck
          </h2>
          <p>
            Es el chat del agente. Entras con tu cuenta de ProMedic, sin instalar nada, y funciona
            igual en el teléfono y en el computador.
          </p>
          <ul className="agente-ticks">
            <li>Funciona para los cuatro roles, cada uno con sus permisos.</li>
            <li>Puede consultar y también registrar: horas, materiales, fotos, tareas.</li>
            <li>No depende de qué plan de ChatGPT tengas.</li>
          </ul>
          <div className="agente-btn-row">
            <Button variant="primary" onClick={abrirKane} data-testid="agente-abrir-kane">
              <i className="bi bi-robot me-2" aria-hidden="true"></i>
              Abrir Kane ahora
            </Button>
            <a className="btn btn-outline-primary" href={KANE_URL} target="_blank" rel="noopener noreferrer">
              Ir a {KANE_URL.replace(/^https?:\/\//, '')}
            </a>
          </div>
          <p className="agente-note">
            «Abrir Kane ahora» entra con tu sesión de ProMedic. Si abres la dirección por tu
            cuenta, te pedirá «Entrar con ProMedic».
          </p>
        </section>

        {/* ---------- Opción 2: ChatGPT ---------- */}
        <section className="modern-card agente-card" aria-labelledby="agente-chatgpt-t">
          <h2 id="agente-chatgpt-t" className="agente-card-title">
            <span className="agente-hex">2</span> Tu propia suscripción de ChatGPT
          </h2>
          <p>
            Agregas ProMedic como conector y le hablas al agente desde ChatGPT. Ojo: depende de tu
            plan (más abajo) y sólo funciona en la web.
          </p>

          <div className="agente-url" aria-label="Dirección del conector">
            <code data-testid="agente-mcp-url">{MCP_CONNECTOR_URL}</code>
            <Button variant="outline-primary" size="sm" onClick={copiarDireccion}>
              {copiado ? 'Copiada' : 'Copiar'}
            </Button>
          </div>

          <ol className="agente-steps">
            {PASOS_CHATGPT.map((paso, i) => (
              <li key={paso.titulo}>
                <span className="agente-hex">{i + 1}</span>
                <span>
                  <strong>{paso.titulo}.</strong> {paso.detalle}
                </span>
              </li>
            ))}
          </ol>
          <p className="agente-note">
            Pasos verificados en ChatGPT web el {PASOS_VERIFICADOS_EL}. Los nombres de los menús
            cambian con las versiones (OpenAI mezcla «Plugins» y «Apps»); lo que no cambia: modo
            desarrollador, crear, pegar la dirección, entrar con tu cuenta.
          </p>

          <div className="agente-planes" data-testid="agente-planes">
            <h3 className="agente-h3">¿Qué plan de ChatGPT necesito?</h3>
            <ul className="agente-planes-list">
              {PLANES_CHATGPT.confirmado.map((p) => (
                <li key={p.plan}>
                  <strong>{p.plan}.</strong> {p.puede}
                </li>
              ))}
              <li className="agente-planes-warn">
                <strong>{PLANES_CHATGPT.sinConfirmar.planes}: no confirmado.</strong>{' '}
                {PLANES_CHATGPT.sinConfirmar.texto}
              </li>
            </ul>
            <p className="agente-planes-min">{PLANES_CHATGPT.minimo}</p>
            <p className="agente-note">
              Fuentes oficiales de OpenAI, leídas el {FUENTES_CONSULTADAS_EL}:{' '}
              {FUENTES_OPENAI.map((f, i) => (
                <React.Fragment key={f.url}>
                  {i > 0 && ' · '}
                  <a href={f.url} target="_blank" rel="noopener noreferrer">
                    {f.titulo}
                  </a>
                </React.Fragment>
              ))}
              . OpenAI cambia estas reglas seguido: si algo no coincide, manda la documentación.
            </p>
          </div>
        </section>
      </div>

      <section className="modern-card agente-card agente-otros" aria-labelledby="agente-otros-t" data-testid="agente-otros">
        <h2 id="agente-otros-t" className="agente-card-title">Otros asistentes con plan gratuito</h2>
        <p>
          Estos también te dejan agregar ProMedic como conector propio sin pagar. Usa la misma
          dirección de arriba. Pasos según la documentación de cada proveedor, leída el{' '}
          {FUENTES_CONSULTADAS_EL}. No los hemos probado con cuentas reales: si un menú no coincide,
          manda la documentación.
        </p>
        {OTROS_ASISTENTES.map((a) => (
          <details key={a.id} className="agente-otro" data-testid={`agente-otro-${a.id}`}>
            <summary>
              <strong>{a.nombre}</strong>
              <span className={`agente-chip agente-chip--${a.plan.estado}`}>
                {a.plan.estado === 'si' ? 'Gratis: confirmado' : 'Gratis: sin requisito de pago'}
              </span>
            </summary>
            <p>{a.plan.texto}</p>
            <ol className="agente-otro-steps">
              {a.pasos.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </ol>
            <ul className="agente-otro-limites">
              {a.limites.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
            <p className="agente-note">
              Fuente oficial, leída el {FUENTES_CONSULTADAS_EL}:{' '}
              {a.fuentes.map((f, i) => (
                <React.Fragment key={f.url}>
                  {i > 0 && ' · '}
                  <a href={f.url} target="_blank" rel="noopener noreferrer">
                    {f.titulo}
                  </a>
                </React.Fragment>
              ))}
            </p>
          </details>
        ))}

        <h3 className="agente-h3">No disponibles o sin confirmar</h3>
        <ul className="agente-no-disp" data-testid="agente-no-disponibles">
          {ASISTENTES_NO_DISPONIBLES.map((n) => (
            <li key={n.nombre}>
              <strong>{n.nombre}.</strong> {n.motivo}{' '}
              <a href={n.url} target="_blank" rel="noopener noreferrer">
                Fuente
              </a>
            </li>
          ))}
        </ul>
      </section>

      <section className="modern-card agente-card agente-mas" aria-labelledby="agente-mas-t">
        <h2 id="agente-mas-t" className="agente-card-title">Más ayuda</h2>
        <ul className="agente-links">
          <li>
            <Link to="/agente/habilidades">Qué sabe hacer tu agente, por rol</Link>
          </li>
          <li>
            <a href={publicUrl('/guia-agente/conectar.html')} target="_blank" rel="noopener noreferrer">
              Página para compartir la conexión (con QR; también Claude, Cursor y VS Code)
            </a>
          </li>
          <li>
            <a href={publicUrl('/guia-agente/')} target="_blank" rel="noopener noreferrer">
              Guía de uso del agente
            </a>
          </li>
        </ul>
      </section>
    </Container>
  );
};

export default AgenteConexion;
